import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CognitionIllustration } from "../../Assets/Elements/Completion/Cognition.svg";
import { ReactComponent as MotivationIllustration } from "../../Assets/Elements/Completion/Motivation.svg";
import { ReactComponent as ResolutionIllustration } from "../../Assets/Elements/Completion/Resolution.svg";
import { ReactComponent as JudgmentIllustration } from "../../Assets/Elements/Completion/Judgment.svg";
import { ReactComponent as DomainIcon } from "../../Assets/icon/DomainIcon.svg";
import { getElements } from "../../Services/elements";

const ILLUSTRATIONS = {
  Cognition: CognitionIllustration,
  Motivation: MotivationIllustration,
  Resolution: ResolutionIllustration,
  Judgment: JudgmentIllustration,
};

const DOMAIN_COPY = {
  Identity: {
    headline: "You've unlocked how you show up",
    subtitle: "Aris now understands your natural pattern of how you show up.",
    systemBody: "They reflect the surface patterns of how you operate.",
  },
  Cognition: {
    headline: "You've unlocked how you think",
    subtitle: "Aris now understands how you process and make sense of what's happening.",
    systemBody: "They shape how you process information.",
  },
  Resolution: {
    headline: "You've unlocked how you handle conflict",
    subtitle: "Aris now understands how you respond to tension and move through difficult moments.",
    systemBody: "They shape how you respond under pressure.",
  },
  Motivation: {
    headline: "You've unlocked what drives you",
    subtitle: "Aris now understands what energizes you and shapes what matters most.",
    systemBody: "They shape what drives and sustains you.",
  },
  Judgment: {
    headline: "You've unlocked how you make decisions",
    subtitle: "Aris now understands how you evaluate options and arrive at decisions.",
    systemBody: "They reflect how you weigh choices, commit, and act.",
  },
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M5 12.5l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// The existing /elements API keys Judgment as "Judgement" (extra 'e').
// The new /elements/overview API uses "Judgment". Normalize both spellings
// plus a lowercase fallback so thumbnails resolve regardless of which side
// the lookup came from.
const DOMAIN_KEY_ALIASES = {
  Judgment: ["Judgment", "Judgement", "judgment", "judgement"],
  Identity: ["Identity", "identity"],
  Cognition: ["Cognition", "cognition"],
  Resolution: ["Resolution", "resolution"],
  Motivation: ["Motivation", "motivation", "Motives", "motives"],
};

const pickThumbnails = (elementsResponse, domainKey) => {
  if (!elementsResponse || !domainKey) return [];
  const original = elementsResponse?.payload?.elements_original
    || elementsResponse?.elements_original
    || {};

  const aliases = DOMAIN_KEY_ALIASES[domainKey] || [domainKey];
  let list = [];
  for (const alias of aliases) {
    if (Array.isArray(original[alias]) && original[alias].length > 0) {
      list = original[alias];
      break;
    }
  }

  return list
    .map((el) => ({
      key: el.key || el.element_key || el.id,
      url: el.element_url || el.icon || el.image,
      name: el.name || el.title,
    }))
    .filter((el) => el.url)
    .slice(0, 3);
};

const DomainCompletionView = ({ item, onClose }) => {
  const navigate = useNavigate();
  const domainKey = item?.domain_key;
  const Illustration = domainKey ? ILLUSTRATIONS[domainKey] : null;
  const copy = DOMAIN_COPY[domainKey] || {
    headline: item?.title ? `You've unlocked ${item.title.toLowerCase()}` : "Unlocked",
    subtitle: item?.description || "",
    systemBody: "Now part of how Aris understands you.",
  };

  const [thumbnails, setThumbnails] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!domainKey) return;
    (async () => {
      try {
        const data = await getElements();
        if (cancelled) return;
        setThumbnails(pickThumbnails(data, domainKey));
      } catch (_) {
        // non-fatal — render without thumbnails
      }
    })();
    return () => { cancelled = true; };
  }, [domainKey]);

  const handleSeeElements = () => {
    if (typeof onClose === "function") onClose();
    const target = domainKey ? `/elements?domain=${encodeURIComponent(domainKey)}` : "/elements";
    navigate(target);
  };

  return (
    <div className="ye-completion">
      <div className="ye-completion-illustration">
        {Illustration
          ? <Illustration />
          : <div className="ye-completion-illustration-fallback" />}
      </div>

      <div className="ye-completion-text-block">
        <h2 className="ye-completion-title">{copy.headline}</h2>
        {copy.subtitle && <p className="ye-completion-subtitle">{copy.subtitle}</p>}
        <span className="ye-completion-status">
          <CheckIcon /> Completed
        </span>
      </div>

      <div className={`ye-completion-elements-card ${domainKey ? `domain-${domainKey}` : ""}`.trim()}>
        <p className="ye-completion-elements-title">These are your elements</p>
        <div className="ye-completion-element-row">
          {thumbnails.length === 0 ? (
            // Render 3 empty slots to keep visual rhythm while loading
            [0, 1, 2].map((i) => (
              <div className="ye-completion-element-thumb" key={i} />
            ))
          ) : (
            thumbnails.map((t) => (
              <div className="ye-completion-element-thumb" key={t.key}>
                <img src={t.url} alt={t.name || ""} />
              </div>
            ))
          )}
        </div>
        <p className="ye-completion-more-link">+ More to explore</p>
      </div>

      <div className="ye-completion-system-block">
        <p className="ye-completion-system-title">Now part of your system</p>
        <p className="ye-completion-system-body">{copy.systemBody}</p>
      </div>

      <button type="button" className="ye-completion-cta" onClick={handleSeeElements}>
        <DomainIcon /> See Your Elements
      </button>
    </div>
  );
};

export default DomainCompletionView;
